import cx from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useContext, useMemo } from 'react'

import {
  SanityProductFragment,
  SanityProductOption,
} from '@data/sanity/queries/types/product'
import { fadeAnimationWithDelay } from '@lib/animate'
import { hasObject } from '@lib/helpers'
import { getOptionLabel } from '@lib/product'
import {
  PartialProductVariantOption,
  ProductVariantSelectionContext,
} from '@lib/product-variant-selection-context'
import { StringsContext } from '@lib/strings-context'

import RadioGroup from '@components/radio-group'
import ProductOptionValue from './product-option-value'

interface ProductFormItemProps {
  product: SanityProductFragment
  option: SanityProductOption
}

const ProductFormItem = ({ product, option }: ProductFormItemProps) => {
  const {
    setVariantUrlParameter,
    setSelectedOptions,
    selectedOptions,
    selectedVariant,
  } = useContext(ProductVariantSelectionContext)
  const strings = useContext(StringsContext)

  const isColorList = useMemo(() => {
    return option.values.every((optionValue) => {
      const optionSetting = product.optionSettings?.find((optionSetting) => {
        const optionParts = optionSetting.forOption?.split(':')

        return (
          optionParts?.[0] === option.name && optionParts?.[1] === optionValue
        )
      })

      return optionSetting?.color
    })
  }, [option, product])

  const value = useMemo(() => {
    const selectedOption = selectedVariant?.options?.find((variantOption) => {
      return variantOption.name === option.name
    })

    return selectedOption?.value ?? ''
  }, [option.name, selectedVariant])

  const errorMessage = useMemo(() => {
    const isValueSelected = !!selectedOptions?.some((selectedOption) => {
      return selectedOption.name === option.name
    })

    if (isValueSelected) {
      return
    }

    if (option.name === 'Color') {
      return strings.productOptionChooseColorMessage
    }

    if (option.name === 'Size') {
      return strings.productOptionChooseSizeMessage
    }
  }, [option, selectedOptions, strings])

  const handleChange = useCallback(
    (newValue: string) => {
      const newSelectedOptions: PartialProductVariantOption[] = [
        ...(selectedOptions?.filter(
          (selectedOption) => selectedOption.name !== option.name,
        ) ?? []),
        {
          name: option.name,
          value: newValue,
        },
      ]
      setSelectedOptions(newSelectedOptions)

      // Find variant that matches new options
      const newVariant = product.variants?.find((variant) =>
        variant.options.every((variantOption) =>
          hasObject(newSelectedOptions, variantOption),
        ),
      )

      if (newVariant && newVariant.id !== selectedVariant?.id) {
        setVariantUrlParameter(newVariant.id)
      }
    },
    [
      product.variants,
      option.name,
      selectedOptions,
      selectedVariant,
      setSelectedOptions,
      setVariantUrlParameter,
    ],
  )

  const label = getOptionLabel(product.optionNames ?? [], option)

  return (
    <div className="flex flex-col gap-y-2.5">
      <div className="text-sm leading-tight font-semibold uppercase">
        {label || option.name}:
      </div>

      <RadioGroup
        value={value}
        onChange={handleChange}
        className={cx('flex flex-wrap gap-y-2', {
          'gap-x-4': isColorList,
          'gap-x-2': !isColorList,
        })}
      >
        {option.values.map((optionValue) => {
          const isActive = !!selectedOptions?.some(
            (selectedOption) =>
              selectedOption.name === option.name &&
              selectedOption.value === optionValue,
          )

          return (
            <ProductOptionValue
              key={optionValue}
              value={optionValue}
              product={product}
              option={option}
              optionSettings={product.optionSettings ?? []}
              strictMatch
              swatchSize={28}
              labelClassName={cx(
                'inline-flex items-center justify-center',
                'px-4 py-1.5 border border-black rounded-full',
                'font-semibold leading-tight text-base text-center',
                'transition-all duration-300',
                {
                  'text-black bg-transparent hover:bg-black hover:text-white':
                    !isActive,
                  'text-white bg-black': isActive,
                },
              )}
            />
          )
        })}
      </RadioGroup>

      <AnimatePresence mode="wait">
        {!!errorMessage && (
          <motion.p
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimationWithDelay}
            className="px-2.5 py-1 text-base leading-tight bg-yellow-light"
          >
            {errorMessage}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProductFormItem
